// src/components/NewsList.jsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { fetchNewsPage } from "../api";
import NewsCard from "./NewsCard";

const NewsList = ({ category, displayTitleMap }) => {
  const [items, setItems] = useState([]);
  const [nextRef, setNextRef] = useState("start.json");
  const [loadingPage, setLoadingPage] = useState(false);
  const [endReached, setEndReached] = useState(false);
  const [error, setError] = useState(null);

  const loadMoreRef = useRef(null);

  // guards to prevent duplicate loads
  const loadingRef = useRef(false);
  const endReachedRef = useRef(false);

  // ✅ track fetched pages so we don't fetch same nextRef again
  const fetchedPagesRef = useRef(new Set());

  const loadPage = useCallback(
    async (page) => {
      if (!page) return;
      if (loadingRef.current || endReachedRef.current) return;

      // ✅ prevent repeated fetch of same page ref
      const uniqueKey = `${category}::${page}`;
      if (fetchedPagesRef.current.has(uniqueKey)) return;

      try {
        loadingRef.current = true;
        setLoadingPage(true);

        // mark fetched BEFORE request to avoid race double-calls
        fetchedPagesRef.current.add(uniqueKey);

        const { stories, next } = await fetchNewsPage(category, page);

        if (!Array.isArray(stories) || stories.length === 0) {
          setEndReached(true);
          endReachedRef.current = true;
          setNextRef("");
          return;
        }

        // append new stories
        setItems((prev) => [...prev, ...stories]);

        // next ref logic
        if (next && String(next).trim() !== "") {
          setNextRef(next);
        } else {
          setNextRef("");
          setEndReached(true);
          endReachedRef.current = true;
        }
      } catch (err) {
        console.error("Error loading news page", err);

        // allow retry if fetch failed
        fetchedPagesRef.current.delete(uniqueKey);

        if (err.status === 404) {
          setEndReached(true);
          endReachedRef.current = true;
          setNextRef("");
        } else {
          setError("या सदरात नवीन बातम्या आम्ही लवकरच घेऊन येऊ !");
        }
      } finally {
        loadingRef.current = false;
        setLoadingPage(false);
      }
    },
    [category]
  );

  // ✅ reset when category changes and load first page
  useEffect(() => {
    setItems([]);
    setNextRef("start.json");
    setEndReached(false);
    setError(null);

    loadingRef.current = false;
    endReachedRef.current = false;
    fetchedPagesRef.current = new Set();

    loadPage("start.json");
  }, [category, loadPage]);

  // ✅ infinite scroll using nextRef
  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          !loadingRef.current &&
          !endReachedRef.current &&
          nextRef
        ) {
          loadPage(nextRef);
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [nextRef, loadPage]);

  const heading = displayTitleMap[category] || "बातम्या";

  // ✅ prepare a random top pool from current visible list
  const shuffledForRelated = useMemo(() => {
    const top = items.slice(0, 10); // take top 10 from current list
    const a = [...top];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }, [items]);

  return (
    <section>
      <h2 style={{ fontSize: 18, margin: "4px 4px 10px" }}>{heading}</h2>

      <div className="news-list">
        {items.map((item, idx) => {
          // ✅ build related list per card
          const relatedStories = shuffledForRelated
            .filter((s) => s.uniq_link !== item.uniq_link)
            .slice(0, 6);

          return (
            <NewsCard
              key={item.uniq_link || idx}
              item={item}
              index={idx}
              category={category}
              relatedStories={relatedStories}
            />
          );
        })}
      </div>

      {loadingPage && (
        <div className="loading">आणखी बातम्या लोड होत आहेत...</div>
      )}
      {error && <div className="error-message">{error}</div>}

      {endReached && (
        <div className="loading" style={{ marginTop: 12 }}>
         
        </div>
      )}

      {/* sentinel */}
      <div ref={loadMoreRef} style={{ height: 1 }} />
    </section>
  );
};

export default NewsList;
